import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { TAG } from '@/constants';
import { buildQueryString } from '@/utils';

const EU6_CALCULATOR_URL = 'eu6-calculator/api/v1';

/** Matches backend GET /develop/eu6-calculator/api/v1/buying-point-options */
export interface BuyingPointTradingDeal {
  name: string;
  percentage?: number;
  isActive?: boolean;
}

export interface BuyingPointOptionsResponse {
  tradingDeals: BuyingPointTradingDeal[];
  count: number;
}

export interface EU6CalculatorSettings {
  totalBudget: number;
  sellCPM: number;
  displayCurrency: 'USD' | 'EUR' | 'GBP' | 'CAD';
  buyingPoint: 'Programmatic' | 'Direct' | 'Social';
  tradingDealPercentage: number;
  hardCostsTotal: number;
}

export interface EU6CalculatorRateCard {
  country: string;
  channel: string;
  publisher: string;
  format: string;
  deliveryPercentage: number;
  rowCurrency: 'USD' | 'EUR' | 'GBP' | 'CAD';
  buyCPM: number;
  dspFeePercentage: number;
}

export interface EU6CalculatorPlan {
  _id?: string;
  name: string;
  description?: string;
  settings: EU6CalculatorSettings;
  rateCards: EU6CalculatorRateCard[];
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GetEU6CalculatorPlanListQuery {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 1 | -1;
}

export interface EU6CalculatorPlanListResponse {
  count: number;
  data: EU6CalculatorPlan[];
}

// Define a service using a base URL and expected endpoints
export const eu6CalculatorApi = createApi({
  reducerPath: 'eu6CalculatorApi',
  tagTypes: [TAG.EU6_CALCULATOR],
  baseQuery,
  endpoints: (builder) => ({
    getBuyingPointOptions: builder.query<BuyingPointOptionsResponse, void>({
      query: () => `${EU6_CALCULATOR_URL}/buying-point-options`,
      providesTags: () => [TAG.EU6_CALCULATOR]
    }),
    getEU6CalculatorPlanList: builder.query<EU6CalculatorPlanListResponse, GetEU6CalculatorPlanListQuery>({
      query: (params) => `${EU6_CALCULATOR_URL}?${buildQueryString(params as Record<string, unknown>)}`,
      providesTags: () => [TAG.EU6_CALCULATOR]
    }),
    getEU6CalculatorPlanById: builder.query<EU6CalculatorPlan, string>({
      query: (id) => `${EU6_CALCULATOR_URL}/${id}`,
      providesTags: () => [TAG.EU6_CALCULATOR]
    }),
    createEU6CalculatorPlan: builder.mutation<
      EU6CalculatorPlan,
      Omit<EU6CalculatorPlan, '_id' | 'createdBy' | 'createdAt' | 'updatedAt'>
    >({
      query: (plan) => ({
        url: EU6_CALCULATOR_URL,
        method: 'POST',
        body: plan
      }),
      invalidatesTags: [TAG.EU6_CALCULATOR]
    }),
    updateEU6CalculatorPlan: builder.mutation<EU6CalculatorPlan, EU6CalculatorPlan>({
      query: ({ _id, ...rest }) => ({
        url: `${EU6_CALCULATOR_URL}/${_id}`,
        method: 'PUT',
        body: rest
      }),
      invalidatesTags: [TAG.EU6_CALCULATOR]
    }),
    deleteEU6CalculatorPlan: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `${EU6_CALCULATOR_URL}/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [TAG.EU6_CALCULATOR]
    })
  })
});

// Export hooks for usage in functional components
export const {
  useGetBuyingPointOptionsQuery,
  useGetEU6CalculatorPlanListQuery,
  useGetEU6CalculatorPlanByIdQuery,
  useCreateEU6CalculatorPlanMutation,
  useUpdateEU6CalculatorPlanMutation,
  useDeleteEU6CalculatorPlanMutation
} = eu6CalculatorApi;
